<script setup>
import { computed, ref } from 'vue'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { useChecklistItemsStore } from '../../stores/checklistItemsStore.js'
import { resolveChecklistNotesPayload } from '../../utils/checklistModalState.js'

const props = defineProps({
  checklistId: {
    type: [Number, String],
    required: true,
  },
})

const { componentRef: dialog, openModal: openDialog, closeModal: closeDialog } = useModalControl()
const checklistItemsStore = useChecklistItemsStore(`checklist_${props.checklistId}`)

const checklistItem = ref(null)
const editNotes = ref('')
const errorMessage = ref(null)

const fieldControlBaseClass = 'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none transition focus-visible:ring-1 focus-visible:ring-ring'

const isSaving = computed(() => {
  if (!checklistItem.value) {
    return false
  }

  return checklistItemsStore.isUpdatingNotes(checklistItem.value.id)
})

const canSubmit = computed(() => {
  return checklistItem.value !== null && !isSaving.value
})

const modalDescription = computed(() => {
  return checklistItem.value ? itemTitle(checklistItem.value) : ''
})

const itemTitle = (item) => {
  return item.instruction ?? `Checklist item #${item.id}`
}

const resetModal = () => {
  checklistItem.value = null
  editNotes.value = ''
  errorMessage.value = null
}

const openModal = (item) => {
  if (!item) {
    return
  }

  checklistItem.value = item
  editNotes.value = item.notes ?? ''
  errorMessage.value = null
  openDialog()
}

const closeModal = () => {
  closeDialog()
}

const handleOk = async (event) => {
  event?.preventDefault?.()

  if (!checklistItem.value) {
    return
  }

  errorMessage.value = null

  try {
    await checklistItemsStore.updateNotes(checklistItem.value.id, resolveChecklistNotesPayload(editNotes.value))
    closeDialog()
  } catch (error) {
    errorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to save checklist notes.'
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Edit Checklist Item Notes"
    :description="modalDescription"
    ok-title="Save"
    cancel-title="Cancel"
    :ok-disabled="!canSubmit"
    @ok="handleOk"
    @hide="resetModal"
  >
    <div class="flex flex-col gap-3">
      <Field>
        <FieldLabel for="checklist-item-edit-notes">
          Notes
        </FieldLabel>
        <textarea
          id="checklist-item-edit-notes"
          v-model="editNotes"
          rows="4"
          :class="fieldControlBaseClass"
          placeholder="Add checklist execution notes..."
        />
      </Field>

      <FieldDescription v-if="errorMessage" class="text-destructive font-medium">
        {{ errorMessage }}
      </FieldDescription>
    </div>
  </UmisisModal>
</template>
